import React from 'react';
import { Truck, Package, RotateCcw, Clock } from 'lucide-react';
import Header from './components/Header20';
import Footer from './components/Footer20';

const ShippingAndExchange = () => {
    const shippingOptions = [
        {
            icon: Truck,
            title: 'Standard Shipping',
            time: '5-7 Business Days',
            cost: 'Free on orders over $50'
        },
        {
            icon: Package,
            title: 'Express Shipping',
            time: '2-3 Business Days',
            cost: '$15.00'
        },
        {
            icon: Clock,
            title: 'Next Day Delivery',
            time: '1 Business Day',
            cost: '$25.00'
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            <Header />

            {/* Hero Section */}
            <section className="bg-gray-50 py-16 lg:py-20">
                <div className="container mx-auto px-4 lg:px-8 text-center">
                    <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Truck className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
                        Shipping & Returns
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Everything you need to know about our shipping options and return policy
                    </p>
                </div>
            </section>

            {/* Shipping Options */}
            <section className="py-16 lg:py-20">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="max-w-5xl mx-auto">
                        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center tracking-tight">
                            Shipping Options
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                            {shippingOptions.map((option, index) => {
                                const Icon = option.icon;
                                return (
                                    <div key={index} className="text-center p-6 border border-gray-200 rounded-lg hover:shadow-lg transition-shadow">
                                        <div className="w-12 h-12 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-2 tracking-tight">
                                            {option.title}
                                        </h3>
                                        <p className="text-gray-600 mb-1">{option.time}</p>
                                        <p className="text-gray-900 font-medium">{option.cost}</p>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Shipping Information */}
                        <div className="space-y-12">
                            <div>
                                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 tracking-tight">
                                    Shipping Information
                                </h2>
                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 tracking-tight">
                                            Processing Time
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            All orders are processed within 1-2 business days. Orders are not shipped or delivered on weekends or holidays.
                                            If we are experiencing a high volume of orders, shipments may be delayed by a few days. Please allow additional days in transit for delivery.
                                        </p>
                                    </div>

                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 tracking-tight">
                                            Order Tracking
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            You will receive a shipping confirmation email with your tracking number once your order has shipped. You can track your order status
                                            through the link provided in the email or by logging into your account on our website.
                                        </p>
                                    </div>

                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 tracking-tight">
                                            International Shipping
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            We currently ship to select countries worldwide. International shipping costs and delivery times vary by destination.
                                            Customs duties and taxes may apply and are the responsibility of the recipient. Please check with your local customs office for more information.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Returns & Exchanges */}
                            <div className="border-t border-gray-200 pt-12">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-12 h-12 bg-gray-900 rounded-full flex items-center justify-center">
                                        <RotateCcw className="w-6 h-6 text-white" />
                                    </div>
                                    <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight">
                                        Returns & Exchanges
                                    </h2>
                                </div>

                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 tracking-tight">
                                            30-Day Return Policy
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            We accept returns within 30 days of delivery for a full refund or exchange. Items must be unworn, unwashed, and in their original condition
                                            with all tags attached. Sale items are final sale and cannot be returned or exchanged.
                                        </p>
                                    </div>

                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 tracking-tight">
                                            How to Return
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed mb-3">
                                            To initiate a return:
                                        </p>
                                        <ol className="list-decimal list-inside space-y-2 text-gray-600 leading-relaxed ml-4">
                                            <li>Log in to your account and go to your order history</li>
                                            <li>Select the order you wish to return and click "Request Return"</li>
                                            <li>Print the prepaid return label we email you</li>
                                            <li>Package your items securely with the original packaging if possible</li>
                                            <li>Attach the return label and drop off at any authorized shipping location</li>
                                        </ol>
                                    </div>

                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 tracking-tight">
                                            Exchanges
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            If you need a different size or color, we recommend placing a new order for the item you want and returning the original item for a refund.
                                            This ensures you get your new item as quickly as possible. If you prefer a direct exchange, please contact our customer service team.
                                        </p>
                                    </div>

                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 tracking-tight">
                                            Refund Processing
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            Once we receive your return, please allow 5-7 business days for processing. Refunds will be issued to your original payment method.
                                            You will receive an email confirmation once your refund has been processed. Please note that it may take an additional 5-10 business days
                                            for the refund to appear in your account, depending on your financial institution.
                                        </p>
                                    </div>

                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 tracking-tight">
                                            Damaged or Defective Items
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            If you receive a damaged or defective item, please contact us immediately at returns@studio.com with your order number and photos of the damage.
                                            We will arrange for a replacement or full refund at no cost to you, including return shipping.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contact Section */}
                        <div className="mt-12 lg:mt-16 p-6 lg:p-8 bg-gray-50 rounded-lg">
                            <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3 tracking-tight">
                                Need Help?
                            </h3>
                            <p className="text-gray-600 mb-4 leading-relaxed">
                                If you have any questions about shipping or returns, our customer service team is here to help:
                            </p>
                            <p className="text-gray-900 font-medium">
                                Email: support@studio.com<br />
                                Phone: 1-800-STUDIO-1 (Mon-Fri, 9am-6pm EST)
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ShippingAndExchange;
